import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [servicesDropdown, setServicesDropdown] = useState(false);
  const [eventsDropdown, setEventsDropdown] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { 
      name: 'Services', 
      path: '/services',
      dropdown: [
        { name: 'Trade Fair Stall Building', path: '/services/stall-building' },
        { name: 'Corporate Events', path: '/services/corporate-events' },
        { name: 'Live Events', path: '/services/live-events' },
        { name: 'Brand Activation', path: '/services/brand-activation' },
      ]
    },
    { 
      name: 'Events', 
      path: '/events',
      dropdown: [
        { name: 'India Retail Investors Expo', path: '/events/retail-investors-expo' },
        { name: 'The FinRun', path: '/events/finrun' },
      ]
    },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      data-testid="navbar"
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg py-3' 
          : 'bg-white/90 backdrop-blur-sm py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-4" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_defense-comms/artifacts/auvagmgz_DFAIRS%20LOGO%20-%20NEW%20BLACK.png" 
              alt="DFAIRS Communications" 
              className="h-16 w-auto"
            />
            <span className="hidden xl:block font-heading text-navy text-lg font-semibold tracking-wide">
              DFAIRS Communications
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <div key={link.name} className="relative">
                {link.dropdown ? (
                  <div 
                    className="relative"
                    onMouseEnter={() => link.name === 'Services' ? setServicesDropdown(true) : setEventsDropdown(true)}
                    onMouseLeave={() => link.name === 'Services' ? setServicesDropdown(false) : setEventsDropdown(false)}
                  >
                    <button 
                      className={`nav-link flex items-center gap-1 ${isActive(link.path) ? 'active' : ''}`}
                      data-testid={`nav-${link.name.toLowerCase()}`}
                    >
                      {link.name}
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    
                    {((link.name === 'Services' && servicesDropdown) || (link.name === 'Events' && eventsDropdown)) && (
                      <div className="absolute top-full left-0 mt-2 w-64 bg-white shadow-xl border border-gray-100 py-2 animate-fade-in">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.name}
                            to={item.path}
                            className="block px-5 py-3 text-sm text-navy hover:bg-muted hover:text-gold transition-colors"
                            data-testid={`nav-dropdown-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link 
                    to={link.path} 
                    className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                    data-testid={`nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link 
              to="/contact" 
              className="btn-primary inline-block text-sm"
              data-testid="nav-cta-button"
            >
              Get Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2 text-navy"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-[72px] bg-white z-40 overflow-y-auto" data-testid="mobile-menu">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <div key={link.name}>
                {link.dropdown ? (
                  <div className="space-y-2">
                    <span className="block text-navy font-semibold uppercase tracking-wider text-sm">
                      {link.name}
                    </span>
                    <div className="pl-4 space-y-2 border-l-2 border-gold">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className="block text-muted-foreground hover:text-navy transition-colors py-2"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link 
                    to={link.path}
                    className={`block py-3 text-lg font-medium ${isActive(link.path) ? 'text-gold' : 'text-navy'}`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            <div className="pt-6">
              <Link 
                to="/contact" 
                className="btn-primary block text-center"
              >
                Get Quote
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
